import { gql } from '@apollo/client';

const menuCategoryFields = gql`
  fragment MenuCategoryFields on MenuCategory {
    id
    name
    position
    applyTowardMinimum
    salesCategory {
      id
      name
    }
    taxes {
      id
      enabled
      name
      rate
    }
    fees {
      id
      enabled
      name
      rate
    }
  }
`;
const menuItemFields = gql`
  fragment MenuItemFields on MenuItem {
    name
    details
    internalDetails
    displayOnGuestMenu
    displayOnExpressBook
    quantitiesOnExtrasEnabled
    quantitiesEditableByGuest
    price
    unit
    position
    image
    addons {
      id
      name
      price
    }
    choices {
      id
      name
      limit
      options {
        id
        name
        price
      }
    }
    taxes {
      id
      enabled
      name
      rate
    }
    fees {
      id
      enabled
      name
      rate
    }
    menuCategory {
      id
      name
    }
  }
`;

const menuFields = gql`
  fragment MenuFields on Menu {
    id
    name
    pdf
    menuCategories {
      id
      ...MenuCategoryFields
    }
    salesCategories {
      id
      name
    }
    menuItems {
      id
      ...MenuItemFields
    }
  }
  ${menuItemFields}
  ${menuCategoryFields}
`;

const venuesQuery = gql`
  query ProviderVenues($ids: [ID!]) {
    venues(ids: $ids) {
      id
      name
      internalName
      canUpdate {
        value
      }
      canListFees {
        value
      }
      canListTaxes {
        value
      }
      canListReports {
        value
      }
      canListMessages {
        value
      }
      canListPayments {
        value
      }
      slug
      email
      image
      customerStartedAt
      banner
      description
      multiDayEnabled
      timezone
      currency
      websiteUrl
      defaultPaymentProvider
      eventStartOffset
      eventEndOffset
      defaultDepositRate
      defaultDepositAmount
      emailApiTockAddress
      emailApiTheKnotAddress
      emailApiWeddingWireAddress
      emailApiOpenTableAddress
      achEnabled
      showPvBranding
      defaultPaymentMethodMechanism
      countersignEnabled
      widgetInstanceId
      widgetContactFormRedirectUrl
      gtmId
      paymentsEnabled
      merriId
      prismmId
      opentableConnected
      featureScope {
        id
        achEnabled
        basicFeaturesEnabled
        proFeaturesEnabled
        premiumFeaturesEnabled
        enterpriseFeaturesEnabled
        floorplanEditorEnabled
        multipleProposalsEnabled
        eventbriteIntegration
        cventIntegration
      }
      addOnPlans {
        id
        active
        trialing
        trialExpired
        addOnDefinition {
          slug
        }
      }
      defaultMinimumAmount
      helloLeadFormPath
      helloLeadFormUrl
      qrcodePngUrl
      spaceGraph
      stripeAccount {
        id
        name
        stripeAccountId
      }
      squareAccount {
        id
        squareMerchantId
      }
      squareLocationId
      minGroupSize
      defaultMinimumEnabled
      defaultDiscountEnabled
      contactFormBackgroundColor
      contactFormButtonColor
      createdAt
      address {
        id
        streetAddress1
        streetAddress2
        city
        state
        zip
        country
        formattedAddress
      }
      organization {
        id
        name
        discount
        stripeCardDetail
        billingEmail
      }
      venueMemberships {
        id
        user {
          id
          email
          firstName
          lastName
          fullName
        }
      }
      spaces {
        id
        name
        seated
        standing
        ignoreConflicts
        displayColor
        expressBookEnabled
      }
      subscription {
        id
        planDefinitionId
        planName
        tier
        status
        billingInterval
        monthlyRate
        cardApplicationFeeRate
        achApplicationFeeRate
        stripeSourceId
        endDate
        lockOut
      }
      activePlanTierTrial {
        id
        trialTier
        endDate
        status
        willAutoUpgrade
      }
      defaultEventOwner {
        id
      }
      expressBookSettings {
        id
        enabled
      }
      featureFlags
      smartFieldDefinitions
      venueType {
        id
      }
      aiReplyProvider
      autoGuestGreetingEnabled
      messageSuggestionPreamble
      feedbackEnabled
      displayFeedbackInProfile
    }
  }
`;

const contractItemFields = gql`
  fragment ContractItemFields on ContractItem {
    id
    name
    index
    price
    unit
    discountAmount
    discountRate
    displayOnGuestMenu
    basePrice
    quantity
    total
    details
    internalDetails
    image
    addons {
      id
      name
      price
    }
    choices {
      id
      name
      limit
      options {
        id
        name
        price
      }
    }
    contractTaxes {
      id
      enabled
      name
      rate
      taxId
    }
    contractFees {
      id
      enabled
      name
      rate
      feeId
    }
    optionIds
    addonIds
    menuCategory {
      id
      name
      position
    }
    quantitiesOnExtrasEnabled
    quantitiesEditableByGuest
    addonQuantities
    optionQuantities
    sessionId
  }
`;

const contractFields = gql`
  fragment ContractFields on Contract {
    uuid
    name
    totals
    discountEnabled
    discountAmount
    discountRate
    minimumEnabled
    minimumAmount
    depositRate
    depositAmount
    action
    signature
    signed
    signedAt
    accepted
    venueSignature
    venueSigned
    venueSignedAt
    venueSigner {
      id
      fullName
    }
    locked
    lockCondition
    paymentMethodMechanism
    cardSurchargeRate
    otherPaymentAmount
    foodAndBeverageMinimumId
    venuePolicyHtml
    hasEventPolicy
    countersignEnabled
    effectivePolicySections {
      name
      body
      html
      position
      requiresInitial
      initialData
    }
    canUpdate {
      value
    }
    venuePolicy {
      id
      name
      body
      parentPolicy {
        id
        name
      }
    }
    contractTaxes {
      id
      rate
      enabled
      applyToMinimum
      name
      overrideAmount
      tax {
        id
        name
        rate
        enabled
        applyToMinimum
      }
    }
    contractFees {
      id
      rate
      enabled
      applyToMinimum
      name
      overrideAmount
      contractTaxes {
        id
        rate
        enabled
        name
      }
      fee {
        id
        name
        rate
      }
    }
    contractItems {
      id
      ...ContractItemFields
    }
  }
  ${contractItemFields}
`;

const eventPaymentFields = gql`
  fragment EventPaymentFields on Payment {
    id
    paymentReportTypeLabel
    name
    kind
    amount
    surchargeAmount
    refundAmount
    paidAt
    scheduledAt
    state
    amountMinusRefundAmount
    refunds {
      id
      amount
      reason
      createdAt
    }
  }
`;

export const sessionFields = gql`
  fragment SessionFields on Session {
    id
    name
    description
    startDate
    startOffset
    endDate
    endOffset
    startsAt
    endsAt
    spaces {
      id
      name
      displayColor
      ignoreConflicts
    }
  }
`;

const eventFields = gql`
  fragment EventFields on Event {
    name
    startDate
    startOffset
    endDate
    endOffset
    startTime
    endTime
    tourStartDate
    tourStartOffset
    tourEndOffset
    rehearsalStartDate
    rehearsalStartOffset
    rehearsalEndOffset
    status
    eventTypeName
    groupSize
    budgetOrContractAmount
    budget
    completedAt
    canceledAt
    discardedAt
    refundedAt
    customFieldValues
    smartFieldValues
    utmSource
    utmCampaign
    paymentProvider
    multiDayEnabled
    internalNoteHtml
    proposalNoteHtml
    interpolatedInternalNoteHtml
    interpolatedProposalNoteHtml
    invoiceNumber
    merriId
    prismmId
    origin
    canUpdate {
      value
    }
    canShowEventBudget {
      value
    }
    user {
      id
      email
      fullName
    }
    spaces {
      id
      name
      displayColor
      ignoreConflicts
    }
    sessions {
      ...SessionFields
    }
    planner {
      id
      firstName
      lastName
      fullName
      email
      phone
      pastEventCount
      notes
      account {
        id
        name
        address {
          id
          streetAddress1
          streetAddress2
          city
          state
          zip
          formattedAddress
        }
      }
      paymentMethods {
        id
        type
        last4
        brand
        mechanism
        displayLabel
        paymentProvider
      }
      address {
        id
        streetAddress1
        streetAddress2
        city
        state
        zip
        formattedAddress
      }
    }
    paymentMethods {
      id
      type
      last4
      brand
      mechanism
      displayLabel
      paymentProvider
    }
    depositPayment {
      ...EventPaymentFields
    }
    remainingBalancePayment {
      ...EventPaymentFields
    }
    payments {
      ...EventPaymentFields
    }
    venue {
      id
      squareLocationId
      sevenShiftsId
      opentableRestaurantId
    }
    opentableIntegrationSync {
      id
      type
      status
      externalId
      lastSyncedAt
      errorMessage
      metadata
      createdAt
      updatedAt
    }
    sevenShiftsIntegrationSync {
      id
      type
      status
      externalId
      externalStatus
      lastSyncedAt
      errorMessage
      metadata
      createdAt
      updatedAt
    }
    eventbriteIntegrationSync {
      id
      type
      status
      externalId
      externalStatus
      lastSyncedAt
      errorMessage
      metadata
      createdAt
      updatedAt
    }
    cventIntegrationSync {
      id
      type
      status
      stale
      externalId
      lastSyncedAt
      errorMessage
      metadata
      createdAt
      updatedAt
    }
    opentableSyncEligibility {
      eligible
      blockingReasons {
        key
        message
      }
    }
    sevenShiftsSyncEligibility {
      eligible
      blockingReasons {
        key
        message
      }
    }
    eventbriteSyncEligibility {
      eligible
      blockingReasons {
        key
        message
      }
    }
    cventSyncEligibility {
      eligible
      blockingReasons {
        key
        message
      }
    }
    contracts {
      id
      uuid
      name
      accepted
    }
    review {
      id
      submittedAt
    }
  }
  ${sessionFields}
  ${eventPaymentFields}
`;

const eventQuery = gql`
  query Event2($id: ID!) {
    event(id: $id) {
      id
      ...EventFields
      markedFraudulentAt
      skipFraudDetection
      spaceAvailability
      contract {
        id
        ...ContractFields
      }
    }
  }
  ${eventFields}
  ${contractFields}
`;

const eventContractsQuery = gql`
  query EventContracts($id: ID!) {
    event(id: $id) {
      id
      contracts {
        id
        ...ContractFields
      }
    }
  }
  ${contractFields}
`;

export {
  contractItemFields,
  contractFields,
  eventFields,
  eventQuery,
  eventContractsQuery,
  menuFields,
  menuItemFields,
  menuCategoryFields,
  venuesQuery,
};
